/**
 * Stage Distribution Chart
 * Visualizes funding stage distribution with year-over-year comparison
 * Shows percentage breakdown by stage for each year
 */

import React, { useMemo, useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { formatMonetaryValue, CHART_STYLES } from '../utils/chartUtils';
import {
  stageDistributionService,
  StageDistributionResponse
} from '../services/StageDistributionService';
import { Download, FileSpreadsheet, Pencil } from 'lucide-react';
import { useServiceData } from '../hooks/useChartData';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { ChartMetadata } from './ChartMetadata';
import { BRAND_COLORS, TYPOGRAPHY } from '../types/data';
import { getResponsiveFontSizes, getResponsiveChartHeight } from '../utils/responsiveChartUtils';
import '../styles/chart-components.css';

interface StageDistributionChartProps extends ChartProps {
  timeframe: string;
  weekOffset: number;
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}

type StageDistributionChartWrapperProps = Omit<StageDistributionChartProps, 'data'>;

/**
 * Configuration function for chart options
 */
const getStageDistributionChartOption = (
  props: StageDistributionChartProps & EnhancedChartProps
) => {
  const { data, timeframe, filters } = props;

  if (!data?.data || !data?.years || !data?.stages) {
    return null;
  }

  const stageData = data.data;
  const years = data.years;
  const stages = data.stages;

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'stage-distribution',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  const title = customTitle?.title || 'Funding Stage Distribution by Year';
  const subtitle = customTitle?.subtitle || 'Total funding distribution by stage';

  // Calculate total deal count per year (for labels)
  const totalDealCounts = years.map(year => {
    let total = 0;
    stages.forEach(stage => {
      const yearData = stageData[year]?.[stage];
      if (yearData?.deals) {
        total += yearData.deals;
      }
    });
    return total;
  });

  // Calculate total funding per year for label positioning
  const totalFundingPerYear = years.map(year => {
    let total = 0;
    stages.forEach(stage => {
      const yearData = stageData[year]?.[stage];
      if (yearData?.funding) {
        total += yearData.funding;
      }
    });
    return total;
  });

  // Prepare data for stacked bar chart (using dollar amounts)
  const seriesData: any[] = stages.map(stage => ({
    name: stageDistributionService.getStageName(stage),
    type: 'bar',
    stack: 'total',
    emphasis: {
      focus: 'series'
    },
    itemStyle: {
      color: stageDistributionService.getStageColor(stage)
    },
    data: years.map(year => {
      const yearData = stageData[year]?.[stage];
      return yearData ? yearData.funding : 0;
    })
  }));

  // Add invisible series to position labels at the top of stacks
  // NOTE: barGap: '-100%' overlays this series on the stack WITHOUT adding to Y-axis scale
  seriesData.push({
    name: 'Total Deals Label',
    type: 'bar',
    barGap: '-100%',  // Overlay on existing bars instead of stacking
    silent: true,
    itemStyle: {
      color: 'transparent',
      borderColor: 'transparent'
    },
    label: {
      show: true,
      position: 'top',
      formatter: (params: any) => {
        const dealCount = totalDealCounts[params.dataIndex];
        return `${dealCount} deals`;
      },
      fontSize: 14,
      fontWeight: 600,
      color: BRAND_COLORS.text,
      fontFamily: TYPOGRAPHY.fontFamily
    },
    data: totalFundingPerYear,
    tooltip: {
      show: false
    },
    legendHoverLink: false,
    emphasis: {
      disabled: true
    }
  });

  // Data description for Carta-style line
  const dataDescription = 'Total funding amounts by stage across years';

  // Get responsive font sizes for mobile/tablet/desktop
  const fontSizes = getResponsiveFontSizes();

  return {
    title: {
      text: title,
      subtext: subtitle,
      left: '1%',  // Match UnifiedChartFactory standards
      top: '1%',
      textStyle: {
        fontSize: fontSizes.title,  // Responsive: 18px mobile, 24px tablet, 32px desktop
        fontWeight: 700,
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      subtextStyle: {
        fontSize: fontSizes.subtitle,  // Responsive: 14px mobile, 16px tablet, 20px desktop
        fontWeight: 500,
        color: BRAND_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily
      }
    },
    graphic: [
      // CTA separator line
      {
        type: 'line',
        left: '5%',
        right: '5%',
        bottom: '7%',
        shape: {
          x1: 0,
          y1: 0,
          x2: 1,
          y2: 0
        },
        style: {
          stroke: BRAND_COLORS.border,
          lineWidth: 1
        }
      },
      // CTA text
      {
        type: 'text',
        left: 'center',
        bottom: '3%',
        style: {
          text: '{normal|Subscribe for more cybersecurity market intelligence at }{purple|ReturnOnSecurity.com}',
          fontSize: 20,
          fontFamily: TYPOGRAPHY.fontFamily,
          textAlign: 'center',
          rich: {
            normal: {
              fontSize: 20,
              fontWeight: 400,
              fill: BRAND_COLORS.text
            },
            purple: {
              fontSize: 20,
              fontWeight: 700,
              fill: BRAND_COLORS.primary // #633FF3
            }
          }
        }
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: BRAND_COLORS.background,
      borderColor: BRAND_COLORS.border,
      borderWidth: 1,
      textStyle: {
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      formatter: (params: any) => {
        const year = params[0].axisValue;
        let html = `<div style="font-weight: 700; font-size: 13px; margin-bottom: 6px;">${year}</div>`;

        // Calculate total for percentage
        let totalFunding = 0;
        params.forEach((param: any) => {
          totalFunding += param.value;
        });

        // Sort by legend order (stage order), not by value
        // The params are already in series order which matches stages order
        const sorted = [...params];

        sorted.forEach((param: any) => {
          if (param.value > 0) {
            const stage = param.seriesName;
            const funding = param.value;
            const yearData = stageData[year]?.[stages[param.seriesIndex]];
            const deals = yearData?.deals || 0;
            const pct = totalFunding > 0 ? (funding / totalFunding) * 100 : 0;

            html += `
              <div style="margin: 3px 0; display: flex; align-items: center; gap: 8px; font-size: 11px;">
                <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 2px; flex-shrink: 0;"></span>
                <span style="font-weight: 500; min-width: 60px;">${stage}:</span>
                <span style="font-weight: 700; color: ${BRAND_COLORS.primary};">${formatMonetaryValue(funding)}</span>
                <span style="color: ${BRAND_COLORS.textSecondary};">(${pct.toFixed(1)}%)</span>
              </div>
            `;
          }
        });

        html += `
          <div style="margin-top: 6px; padding-top: 4px; border-top: 1px solid ${BRAND_COLORS.border}; font-size: 11px;">
            <div style="display: flex; justify-content: space-between; font-weight: 700;">
              <span>Total:</span>
              <span>${formatMonetaryValue(totalFunding)}</span>
            </div>
          </div>
        `;

        return html;
      }
    },
    legend: {
      type: 'plain',  // Single-line legend
      orient: 'horizontal',
      bottom: CHART_STYLES.legend.bottom,
      left: CHART_STYLES.legend.alignment,
      textStyle: {
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily,
        fontSize: CHART_STYLES.legend.fontSize,
        fontWeight: CHART_STYLES.legend.fontWeight
      },
      itemGap: CHART_STYLES.legend.itemGap,
      itemWidth: 14,   // Legend icon size
      itemHeight: 14
    },
    grid: {
      left: CHART_STYLES.grid.left,
      right: CHART_STYLES.grid.right,
      top: CHART_STYLES.grid.top,
      bottom: CHART_STYLES.grid.bottom,
      containLabel: CHART_STYLES.grid.containLabel
    },
    xAxis: {
      type: 'category',
      data: years,
      axisLabel: {
        fontSize: CHART_STYLES.xAxis.fontSize,
        fontWeight: CHART_STYLES.xAxis.fontWeight,
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      axisLine: {
        lineStyle: {
          color: BRAND_COLORS.border
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: CHART_STYLES.yAxis.fontSize,
        fontWeight: CHART_STYLES.yAxis.fontWeight,
        color: BRAND_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily,
        formatter: (value: number) => {
          if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
          if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
          if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
          return `$${value}`;
        }
      },
      axisLine: {
        lineStyle: {
          color: BRAND_COLORS.border
        }
      },
      splitLine: {
        lineStyle: {
          color: BRAND_COLORS.border,
          type: 'dashed'
        }
      }
    },
    series: seriesData
  };
};

/**
 * Base chart component that receives data and renders
 */
const StageDistributionChartBase: React.FC<
  StageDistributionChartProps & EnhancedChartProps
> = React.memo(({
  data,
  timeframe,
  filters,
  chartRef,
  isLoading,
  error,
  downloadChart,
  refreshChart,
  metadata,
  ...restProps
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(
    () => ({
      chartType: 'stage-distribution',
      timeframe,
      filters
    }),
    [timeframe, filters]
  );

  // Check for custom title
  const customTitle = React.useMemo(
    () => customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // Refresh chart when forceRefresh changes
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Get chart option for title extraction
  const chartOption = React.useMemo(
    () =>
      getStageDistributionChartOption({
        data,
        timeframe,
        filters
      } as StageDistributionChartProps & EnhancedChartProps),
    [data, timeframe, filters, forceRefresh]
  );

  const titleOption = Array.isArray(chartOption?.title)
    ? chartOption.title[0]
    : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  const hasData = data?.data && data?.years && data?.stages && data.years.length > 0;

  if (error) {
    return (
      <div className="chart-container-error">
        <div className="chart-error">
          <p className="chart-error-title">Error loading stage distribution</p>
          <p className="chart-error-message">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!hasData && !isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3 className="chart-title">Funding Stage Distribution by Year</h3>
        </div>
        <div className="chart-empty">
          <p className="chart-empty-message">
            No stage distribution data available for this timeframe
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="chart-container-loading">
        <div className="chart-loading">
          <div className="chart-loading-spinner"></div>
        </div>
      </div>
    );
  }

  const handleCSVExport = () => {
    if (!data?.data || !data?.years || !data?.stages) return;

    const headers = ['Year', ...data.stages.map(s => stageDistributionService.getStageName(s))];
    const rows = data.years.map((year: string) => {
      const row = [year];
      data.stages.forEach((stage: string) => {
        const stageData = data.data[year]?.[stage];
        row.push((stageData?.funding || 0).toString());
      });
      return row;
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `stage-distribution-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas */}
        <div
          ref={chartRef}
          className="w-full"
          style={{
            height: `${getResponsiveChartHeight(850, 'stage')}px`,  // Responsive: 600px mobile, 680px tablet, 850px desktop
            minHeight: `${getResponsiveChartHeight(850, 'stage')}px`
          }}
        />

        {/* Chart Metadata */}
        {metadata && (
          <ChartMetadata
            periodStart={metadata.current_period?.start}
            periodEnd={metadata.current_period?.end}
            cacheStatus={metadata.cache_status}
            responseTimeMs={metadata.response_time_ms}
          />
        )}
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'stage-distribution',
          timeframe
        }}
      />
    </>
  );
});

/**
 * Data fetching wrapper component
 */
const StageDistributionChartWithData: React.FC<StageDistributionChartWrapperProps> = ({
  timeframe,
  weekOffset,
  filters,
  metadata
}) => {
  const { data, isLoading, error } = useServiceData(
    () => stageDistributionService.getStageDistribution(timeframe, weekOffset, filters),
    [timeframe, weekOffset, filters],
    {
      validationEnabled: false,
      retryOnError: true,
      maxRetries: 3
    }
  );

  const chartMetadata = data
    ? {
        current_period: data.metadata?.current_period || { start: '', end: '' },
        cache_status: data.cache_status,
        response_time_ms: data.response_time_ms
      }
    : undefined;

  return (
    <StageDistributionChartWithHOC
      data={data}
      timeframe={timeframe}
      weekOffset={weekOffset}
      filters={filters}
      metadata={chartMetadata}
      isLoading={isLoading}
      error={error}
    />
  );
};

// Create the HOC-wrapped component
const StageDistributionChartWithHOC = withChartConfiguration(
  StageDistributionChartBase,
  getStageDistributionChartOption
);

export default StageDistributionChartWithData;
